import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import { HTTPError } from "griptonite-api-client/client";
import { ApiQuestEntryStaff } from "griptonite-api-client/types";

import { useBanFromLeaderboardMutation } from "@/hooks/quests";

import { Link } from "../Links";

const errorMessage = (error: Error) =>
  error instanceof HTTPError && error.response.status === 403
    ? "Only venue admins can ban climbers from the leaderboard."
    : "Couldn’t ban them from the leaderboard. Please try again.";

// Bans the entrant from the venue's leaderboard for this month once the user has confirmed. The
// ban disqualifies them from every points quest running now, this one included.
export default function LeaderboardBanModal({
  entry,
  questName,
  venueUuid,
  questUuid,
  onClose,
}: {
  entry: ApiQuestEntryStaff;
  questName: string;
  venueUuid: string;
  questUuid: string;
  onClose: () => void;
}) {
  const mutation = useBanFromLeaderboardMutation(venueUuid, questUuid);
  const name = entry.user.name;

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: "100%", maxWidth: 440 }}>
        <ModalClose />
        <DialogTitle>Remove {name}</DialogTitle>
        {/* The theme centres dialog text, which reads badly over several sentences. */}
        <DialogContent sx={{ textAlign: "left" }}>
          <Stack gap={2} sx={{ pt: 1 }}>
            <Typography level="body-md">
              {name} will be removed from this month’s leaderboard and lose the credits they’ve
              earned from {questName}.
            </Typography>
            <Typography level="body-sm" textColor="neutral.700">
              For one-off cheating or abuse. For repeat offenders,{" "}
              {entry.creditAccountUuid ? (
                <Link
                  to="/gym/$venueUuid/loyalty/accounts/$accountUuid"
                  params={{ venueUuid, accountUuid: entry.creditAccountUuid }}
                >
                  ban their credits account
                </Link>
              ) : (
                "ban their credit account"
              )}{" "}
              instead.
            </Typography>
            {mutation.error ? <Alert color="danger">{errorMessage(mutation.error)}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="danger"
            loading={mutation.isPending}
            onClick={() => mutation.mutate(entry.user.uuid, { onSuccess: onClose })}
          >
            Remove from quest
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
